import { Injectable, InjectionToken, OnDestroy, computed, inject, signal } from '@angular/core';
import type { Socket, io as socketIo } from 'socket.io-client';

/**
 * Loader for the `io` function from `socket.io-client`.
 *
 * Defined as an injection token so tests can substitute a synchronous stub
 * without using `vi.mock('socket.io-client')`. The Angular vitest builder
 * patches `vi.mock` in a way that is incompatible with dynamic-imported
 * modules, so we resolve the dependency through DI instead.
 */
export const SOCKET_IO_LOADER = new InjectionToken<() => Promise<typeof socketIo>>('SOCKET_IO_LOADER', {
  providedIn: 'root',
  factory: () => async () => (await import('socket.io-client')).io,
});

/**
 * WebSocket event payloads matching the server-side notification gateway.
 */
export interface NotificationCreatedEvent {
  id: string;
  title: string;
  message: string;
  type: string;
  priority: string;
  category: string | null;
  metadata: Record<string, unknown> | null;
  createdAt: string;
  read?: boolean;
  readAt?: string;
}

export interface NotificationReadEvent {
  notificationId: string;
  readAt: string;
}

export interface NotificationCountEvent {
  count: number;
}

/**
 * Socket.IO client for real-time notification delivery.
 *
 * Connects to the `/notifications` namespace using cookie-based auth
 * (same as the existing JobsGatewayClient pattern). Maintains signal-based
 * state for unread count and latest notifications.
 *
 * @example
 * ```typescript
 * const gateway = inject(NotificationGatewayClient);
 *
 * // Read unread count (for badge)
 * const count = gateway.unreadCount();
 *
 * // Get latest notifications
 * const latest = gateway.latestNotifications();
 *
 * // Mark a notification as read
 * gateway.markAsRead('notification-id');
 * ```
 */
@Injectable({ providedIn: 'root' })
export class NotificationGatewayClient implements OnDestroy {
  private readonly loadIo = inject(SOCKET_IO_LOADER);
  private socket: Socket | null = null;
  private connectPromise: Promise<void> | null = null;
  private reconnectAttempts = 0;
  private readonly MAX_RECONNECT_ATTEMPTS = 5;
  private readonly MAX_LATEST_NOTIFICATIONS = 50;

  // Signal-based state
  private readonly _connected = signal(false);
  private readonly _unreadCount = signal(0);
  private readonly _latestNotifications = signal<NotificationCreatedEvent[]>([]);
  private readonly _categoryVersions = signal<ReadonlyMap<string, number>>(new Map());
  private readonly seenNotificationIds = new Set<string>();

  // Public readonly signals
  readonly connected = this._connected.asReadonly();
  readonly unreadCount = this._unreadCount.asReadonly();
  readonly latestNotifications = this._latestNotifications.asReadonly();

  /** Whether there are any unread notifications. */
  readonly hasUnread = computed(() => this._unreadCount() > 0);

  categoryVersion(category: string): number {
    return this._categoryVersions().get(category) ?? 0;
  }

  constructor() {
    queueMicrotask(() => this.connect().catch(error => console.error('Failed to connect notification gateway:', error)));
  }

  ngOnDestroy(): void {
    this.disconnect();
  }

  /**
   * Connect to the notifications WebSocket namespace.
   * Uses cookie-based auth (browser automatically sends cookies).
   * Socket.IO client is lazy-loaded on first connect to keep it out of the initial bundle.
   * Concurrent calls are deduplicated via a shared promise.
   */
  connect(): Promise<void> {
    if (this.socket?.connected) {
      return Promise.resolve();
    }

    if (!this.connectPromise) {
      this.connectPromise = this.doConnect();
    }
    return this.connectPromise;
  }

  private async doConnect(): Promise<void> {
    try {
      const io = await this.loadIo();

      this.socket = io('/notifications', {
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        reconnectionAttempts: this.MAX_RECONNECT_ATTEMPTS,
        transports: ['websocket', 'polling'],
      });

      this.setupEventListeners();
    } finally {
      this.connectPromise = null;
    }
  }

  /**
   * Disconnect from the WebSocket.
   */
  disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
    this._connected.set(false);
  }

  // Clears stale auth-scoped state so the next login starts fresh.
  reset(): void {
    this.disconnect();
    this._latestNotifications.set([]);
    this._unreadCount.set(0);
    this.seenNotificationIds.clear();
    this._categoryVersions.set(new Map());
    this.reconnectAttempts = 0;
    this.connectPromise = null;
  }

  reconnect(): Promise<void> {
    this.reset();
    return this.connect();
  }

  /**
   * Mark a notification as read via WebSocket.
   * The server will broadcast `notification:read` and `notification:count` to all sessions.
   */
  markAsRead(notificationId: string): void {
    if (this.socket?.connected) {
      this.socket.emit('notification:mark-read', { notificationId });
    }
  }

  /**
   * Mark all notifications as read via WebSocket.
   * The server will broadcast `notification:count` with count=0.
   */
  markAllAsRead(): void {
    if (this.socket?.connected) {
      this.socket.emit('notification:mark-all-read');
    }
  }

  private recordCategoryVersion({ id, category }: NotificationCreatedEvent): void {
    if (this.seenNotificationIds.has(id)) return;
    this.seenNotificationIds.add(id);
    if (category === null) return;
    this._categoryVersions.update(versions => new Map(versions).set(category, (versions.get(category) ?? 0) + 1));
  }

  private setupEventListeners(): void {
    if (!this.socket) return;

    this.socket.on('connect', () => {
      this._connected.set(true);
      this.reconnectAttempts = 0;
      // Server sends notification:count on connect automatically
    });

    this.socket.on('disconnect', () => {
      this._connected.set(false);
    });

    this.socket.on('connect_error', () => {
      this.reconnectAttempts++;
    });

    this.socket.on('notification:created', (data: NotificationCreatedEvent) => {
      this.recordCategoryVersion(data);
      this._latestNotifications.update(prev => {
        const updated = [data, ...prev];
        // Keep the list bounded
        return updated.length > this.MAX_LATEST_NOTIFICATIONS ? updated.slice(0, this.MAX_LATEST_NOTIFICATIONS) : updated;
      });
    });

    this.socket.on('notification:read', (data: NotificationReadEvent) => {
      this._latestNotifications.update(prev => prev.map(n => (n.id === data.notificationId ? { ...n, read: true } : n)));
    });

    this.socket.on('notification:count', (data: NotificationCountEvent) => {
      this._unreadCount.set(data.count);
    });
  }
}
